import React, { useState, useEffect, useCallback } from 'react'
import { TextInput, ActionIcon, Textarea, Select, NumberInput, Button, Text, Divider, Card, Group, Box, Grid, Loader, Center, SimpleGrid, Image, UnstyledButton, Avatar } from '@mantine/core'
import { IconEdit } from '@tabler/icons-react'
import { notifications } from '@mantine/notifications'
import { IconCheck, IconX } from '@tabler/icons-react'
import {
    getInventory, getChildCategories,
    getUsers, getProducts,
    createInventory, updateInventory
} from '../../../services/inventory'

const getInitials = (name) => {
    if (!name) return ''
    const parts = name.split(' ')
    return parts.length >= 2
        ? (parts[0][0] + parts[1][0]).toUpperCase()
        : parts[0].substring(0, 2).toUpperCase()
}

const defaultFormData = () => ({
    sku: '',
    description: '',
    productName: '',
    product: { id: 0, title: '' },
    warehouseId: 0,
    categoryLvl1: { id: null, name: '' },
    categoryLvl2: { id: null, name: '' },
    categoryLvl3: { id: null, name: '' },
    quantity: 0,
    quantityUnit: 'pcs',
    weight: '',
    weightUnit: 'gr',
    width: '',
    length: '',
    height: '',
    dimensionUnit: 'cm',
    user: { id: 0, name: '' }
})

const FormInventory = ({ idSelected, onClose, onRefresh, warehouseList, quantityUnitList, dimensionUnitList, weightUnitList, mainCategoryList }) => {
    const [formData, setFormData] = useState(defaultFormData())
    const [loadingDetail, setLoadingDetail] = useState(false)
    const [loadingSave, setLoadingSave] = useState(false)
    const [categoryList, setCategoryList] = useState({ level1: mainCategoryList || [], level2: [], level3: [] })
    const [userList, setUserList] = useState([])
    const [inputSearchUser, setInputSearchUser] = useState('')
    const [loadingUsers, setLoadingUsers] = useState(false)
    const [productList, setProductList] = useState([])
    const [inputSearchProduct, setInputSearchProduct] = useState('')
    const [loadingProducts, setLoadingProducts] = useState(false)
    const [showUserSearch, setShowUserSearch] = useState(false)
    const [showProductSearch, setShowProductSearch] = useState(false)
    const [errors, setErrors] = useState({})

    const updateField = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }))
    }



    const fetchInventoryDetail = useCallback(async () => {
        if (!idSelected) return
        setLoadingDetail(true)
        try {
            const res = await getInventory(idSelected)
            const inventory = res?.data?.inventory
            if (!inventory) return

            const { categories, specification, product } = inventory
            const isEmptyCategory = !categories || categories.length === 0
            const isEmptyProduct = !product

            let catLvl1 = { id: null, name: '' }
            let catLvl2 = { id: null, name: '' }
            let catLvl3 = { id: null, name: '' }

            if (!isEmptyCategory) {
                const cats = []
                cats.push(categories[0])
                if (categories[0].parent) {
                    cats.unshift(categories[0].parent)
                    if (categories[0].parent.parent) cats.unshift(categories[0].parent.parent)
                }
                catLvl1 = { id: cats[0]?.id, name: cats[0]?.name || '' }
                catLvl2 = cats.length > 1 ? { id: cats[1]?.id, name: cats[1]?.name || '' } : { id: null, name: '' }
                catLvl3 = cats.length > 2 ? { id: cats[2]?.id, name: cats[2]?.name || '' } : { id: null, name: '' }

                if (catLvl1.id) fetchChildCategories(Number(catLvl1.id), 'level2')
                if (catLvl2.id) fetchChildCategories(Number(catLvl2.id), 'level3')
            }

            setFormData({
                sku: inventory.sku || '',
                description: inventory.description || '',
                productName: inventory.productName || '',
                product: { id: isEmptyProduct ? 0 : Number(product.id), title: isEmptyProduct ? '' : product.title },
                warehouseId: inventory.warehouseId ? Number(inventory.warehouseId) : 0,
                categoryLvl1: catLvl1,
                categoryLvl2: catLvl2,
                categoryLvl3: catLvl3,
                quantity: specification?.quantity || 0,
                quantityUnit: specification?.quantityUnit || 'pcs',
                weight: specification?.weight || '',
                weightUnit: specification?.weightUnit || 'gr',
                width: specification?.width || '',
                length: specification?.length || '',
                height: specification?.height || '',
                dimensionUnit: specification?.dimensionUnit || 'cm',
                user: { id: inventory.user?.id ? Number(inventory.user.id) : 0, name: inventory.user?.name || '' }
            })
        } catch (error) {
            notifications.show({ title: 'Error', message: error.message, color: 'red', icon: <IconX /> })
        } finally {
            setLoadingDetail(false)
        }
    }, [idSelected])

    useEffect(() => {
        if (!idSelected && warehouseList.length > 0) {
            setFormData(prev => ({ ...prev, warehouseId: Number(warehouseList[0].id) }))
        }
    }, [idSelected, warehouseList])

    useEffect(() => {
        if (idSelected) {
            if (quantityUnitList.length > 0) fetchInventoryDetail()
        } else {
            setFormData(defaultFormData())
            setCategoryList(prev => ({ ...prev, level1: mainCategoryList }))
        }
    }, [idSelected, quantityUnitList, fetchInventoryDetail, mainCategoryList])

    const fetchChildCategories = async (parentId, keyLevel) => {
        try {
            const res = await getChildCategories(parentId)
            const data = (res?.data?.categories?.data || []).map(c => ({ id: c.id, name: c.name }))
            setCategoryList(prev => ({ ...prev, [keyLevel]: data }))
        } catch (error) {
            console.error(error)
        }
    }

    const onClickCategory = (category, level) => {
        const updated = { ...formData }
        updated[`categoryLvl${level}`] = { id: category.id, name: category.name }
        if (level === 1) {
            updated.categoryLvl2 = { id: null, name: '' }
            updated.categoryLvl3 = { id: null, name: '' }
            setCategoryList(prev => ({ ...prev, level2: [], level3: [] }))
        } else if (level === 2) {
            updated.categoryLvl3 = { id: null, name: '' }
            setCategoryList(prev => ({ ...prev, level3: [] }))
        }
        setFormData(updated)
        if (level !== 3) fetchChildCategories(Number(category.id), `level${level + 1}`)
    }

    const handleSearchUsers = async () => {
        setLoadingUsers(true)
        try {
            const res = await getUsers(inputSearchUser)
            setUserList((res?.data?.users?.data || []).map(u => ({ id: u.id, name: u.name, email: u.email })))
        } catch (error) { console.error(error) }
        finally { setLoadingUsers(false) }
    }

    const handleSearchProducts = async () => {
        setLoadingProducts(true)
        try {
            const res = await getProducts(inputSearchProduct)
            const getMainImage = (images) => {
                if (images && images.length > 0) {
                    const def = images.find(img => img.is_default)
                    return def ? def.source : ''
                }
                return ''
            }
            setProductList((res?.data?.products?.data || []).map(p => ({ id: p.id, title: p.title, imageUrl: getMainImage(p.images) })))
        } catch (error) { console.error(error) }
        finally { setLoadingProducts(false) }
    }

    const validate = () => {
        const err = {}
        if (!formData.sku) err.sku = true
        if (!formData.productName) err.productName = true
        if (!formData.warehouseId) err.warehouseId = true
        if (formData.quantity === '') err.quantity = true
        if (!formData.user.id) err.user = true
        setErrors(err)
        if (Object.keys(err).length > 0) {
            const msg = err.sku ? 'SKU is Required' : err.productName ? 'Product Name is Required' : err.warehouseId ? 'Warehouse is Required' : err.quantity ? 'Quantity is Required' : 'User Relation is Required'
            notifications.show({ title: 'Validation Error', message: msg, color: 'red', icon: <IconX /> })
            return false
        }
        return true
    }

    const handleSave = async () => {
        if (!validate()) return
        const data = formData
        const payload = {
            user_id: Number(data.user.id),
            warehouse_id: Number(data.warehouseId),
            sku: data.sku,
            product_name: data.productName,
            specification: {
                weight: data.weight ? Number(data.weight) : null,
                weight_unit: weightUnitList.find(e => e.value === data.weightUnit)?.name || null,
                width: data.width ? Number(data.width) : null,
                length: data.length ? Number(data.length) : null,
                height: data.height ? Number(data.height) : null,
                dimension_unit: dimensionUnitList.find(e => e.value === data.dimensionUnit)?.name || null,
                quantity_unit: data.quantityUnit.toUpperCase(),
            },
            description: data.description || null,
        }
        if (idSelected) {
            payload.id = Number(idSelected)
        } else {
            payload.specification.quantity = data.quantity !== '' ? Number(data.quantity) : null
        }
        if (Number(data.categoryLvl3.id)) payload.categories = Number(data.categoryLvl3.id)
        else if (Number(data.categoryLvl2.id)) payload.categories = Number(data.categoryLvl2.id)
        else if (Number(data.categoryLvl1.id)) payload.categories = Number(data.categoryLvl1.id)
        if (Number(data.product.id)) payload.product_id = Number(data.product.id)

        setLoadingSave(true)
        try {
            const res = idSelected ? await updateInventory(payload) : await createInventory(payload)
            if (res?.errors) {
                const msg = res.errors[0]?.message?.includes('Validation') ? 'SKU has already been taken' : 'Cannot save inventory data'
                notifications.show({ title: 'Error', message: msg, color: 'red', icon: <IconX /> })
            } else {
                notifications.show({ title: 'Success', message: 'Data saved successfully', color: 'teal', icon: <IconCheck /> })
                onClose()
                onRefresh()
            }
        } catch (error) {
            notifications.show({ title: 'Error', message: error.message, color: 'red', icon: <IconX /> })
        } finally {
            setLoadingSave(false)
        }
    }

    if (loadingDetail) {
        return <Center py={40}><Loader size="md" /><Text ml="sm" c="dimmed">Getting data...</Text></Center>
    }

    const categoryPath = [formData.categoryLvl1.name, formData.categoryLvl2.name, formData.categoryLvl3.name].filter(Boolean).join(' / ')

    return (
        <Box>
            <Grid>
                <Grid.Col span={6}>
                    <TextInput label={<>SKU <Text component="span" c="red">*</Text></>} placeholder="ex. ALI-XXXXXX" size="sm"
                        value={formData.sku} onChange={(e) => updateField('sku', e.target.value)} error={errors.sku && 'SKU is Required'} />
                </Grid.Col>
                <Grid.Col span={6}>
                    <TextInput label={<>Product Name <Text component="span" c="red">*</Text></>} size="sm"
                        value={formData.productName} onChange={(e) => updateField('productName', e.target.value)} error={errors.productName && 'Product Name is Required'} />
                </Grid.Col>
            </Grid>

            <Textarea label="Description (optional)" mt="sm" value={formData.description || ''} onChange={(e) => updateField('description', e.target.value)} />

            {/* Categories */}
            <Box mt="sm">
                <Text size="sm" fw={500}>Category (optional): {categoryPath}</Text>
                <Box mt={4} p="xs" style={{ border: '1px solid #dee2e6', borderRadius: 8 }}>
                    <Grid>
                        {['level1', 'level2', 'level3'].map((level, idx) => (
                            <Grid.Col key={level} span={4} style={idx < 2 ? { borderRight: '1px solid #dee2e6' } : {}}>
                                <Box style={{ maxHeight: 200, overflowY: 'auto' }}>
                                    {categoryList[level].map(cat => (
                                        <UnstyledButton key={cat.id} onClick={() => onClickCategory(cat, idx + 1)}
                                            p="xs" display="block" w="100%" style={{ borderRadius: 6, background: formData[`categoryLvl${idx + 1}`]?.id === cat.id ? '#e9ecef' : 'transparent' }}>
                                            <Group justify="space-between" wrap="nowrap">
                                                <Text size="xs" fw={formData[`categoryLvl${idx + 1}`]?.id === cat.id ? 700 : 400} truncate="end" style={{ flex: 1 }}>
                                                    {cat.name}
                                                </Text>
                                                {idx < 2 && <Text size="xs" c="dimmed" style={{ flexShrink: 0 }}>›</Text>}
                                            </Group>
                                        </UnstyledButton>
                                    ))}
                                </Box>
                            </Grid.Col>
                        ))}
                    </Grid>
                </Box>
            </Box>

            {/* Warehouse */}
            <Select label={<>Warehouse <Text component="span" c="red">*</Text></>} mt="sm" size="sm"
                data={warehouseList.map(w => ({ value: String(w.id), label: w.name }))}
                value={String(formData.warehouseId)} onChange={(v) => updateField('warehouseId', Number(v))}
                error={errors.warehouseId && 'Warehouse is Required'} />

            {/* Dimensions */}
            <Box mt="sm" p="sm" bg="gray.0" style={{ borderRadius: 8, border: '1px solid #dee2e6' }}>
                <Grid>
                    <Grid.Col span={3}>
                        <Select label={<>Quantity Unit <Text component="span" c="red">*</Text></>} size="sm"
                            data={quantityUnitList.map(q => ({ value: q.value, label: q.label }))}
                            value={formData.quantityUnit} onChange={(v) => updateField('quantityUnit', v)} />
                    </Grid.Col>
                    <Grid.Col span={3}>
                        <NumberInput label={<>Quantity <Text component="span" c="red">*</Text></>} size="sm" min={0}
                            disabled={Boolean(idSelected)} value={formData.quantity} onChange={(v) => updateField('quantity', v)}
                            error={errors.quantity && 'Quantity is Required'} />
                    </Grid.Col>
                    <Grid.Col span={3}>
                        <Select label={<>Weight Unit <Text component="span" c="red">*</Text></>} size="sm"
                            data={weightUnitList.map(w => ({ value: w.value, label: w.value }))}
                            value={formData.weightUnit} onChange={(v) => updateField('weightUnit', v)} />
                    </Grid.Col>
                    <Grid.Col span={3}>
                        <NumberInput label="Weight (optional)" size="sm" min={0} value={formData.weight} onChange={(v) => updateField('weight', v)} />
                    </Grid.Col>
                </Grid>
                <Grid mt="xs">
                    <Grid.Col span={3}>
                        <Select label={<>Dimension Unit <Text component="span" c="red">*</Text></>} size="sm"
                            data={dimensionUnitList.map(d => ({ value: d.value, label: d.value }))}
                            value={formData.dimensionUnit} onChange={(v) => updateField('dimensionUnit', v)} />
                    </Grid.Col>
                    <Grid.Col span={3}><NumberInput label="Width (optional)" size="sm" min={0} value={formData.width} onChange={(v) => updateField('width', v)} /></Grid.Col>
                    <Grid.Col span={3}><NumberInput label="Length (optional)" size="sm" min={0} value={formData.length} onChange={(v) => updateField('length', v)} /></Grid.Col>
                    <Grid.Col span={3}><NumberInput label="Height (optional)" size="sm" min={0} value={formData.height} onChange={(v) => updateField('height', v)} /></Grid.Col>
                </Grid>
            </Box>

            {/* User Owner */}
            <Card mt="sm" shadow="xs" withBorder>
                <Group justify="space-between">
                    <Text fw={700} size="sm">User Owner <Text component="span" c="red">*</Text>
                        {errors.user && <Text component="span" c="red" size="xs"> (User Relation is Required)</Text>}
                    </Text>
                    <ActionIcon variant="subtle" onClick={() => { setShowUserSearch(!showUserSearch); if (!showUserSearch) handleSearchUsers() }}>
                        <IconEdit size={16} />
                    </ActionIcon>
                </Group>
                <Divider my="xs" />
                {formData.user.id ? (
                    <Group>
                        <Avatar color="blue" radius="xl">{getInitials(formData.user.name)}</Avatar>
                        <Text fw={700} size="sm">{formData.user.id} - {formData.user.name}</Text>
                    </Group>
                ) : <Text c="red" size="sm">User not set yet</Text>}
                {showUserSearch && (
                    <Box mt="sm">
                        <TextInput placeholder="Search by name" size="sm" value={inputSearchUser}
                            onChange={(e) => setInputSearchUser(e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter') handleSearchUsers() }} />
                        {loadingUsers ? <Center py="sm"><Loader size="sm" /></Center> : (
                            <Box mt="xs" style={{ maxHeight: 200, overflowY: 'auto' }}>
                                {userList.map(user => (
                                    <UnstyledButton key={user.id} p="xs" display="block" w="100%"
                                        style={{ borderRadius: 6, border: '1px solid #dee2e6', marginBottom: 4 }}
                                        onClick={() => { updateField('user', { id: Number(user.id), name: user.name }); setShowUserSearch(false) }}>
                                        <Group>
                                            <Avatar color="blue" radius="xl" size="sm">{getInitials(user.name)}</Avatar>
                                            <div><Text size="sm" fw={500} tt="capitalize">{user.name}</Text><Text size="xs" c="dimmed">{user.email}</Text></div>
                                        </Group>
                                    </UnstyledButton>
                                ))}
                            </Box>
                        )}
                    </Box>
                )}
            </Card>

            {/* Product Reference */}
            <Card mt="sm" shadow="xs" withBorder>
                <Group justify="space-between">
                    <Text fw={700} size="sm">Product Reference (optional)</Text>
                    <ActionIcon variant="subtle" onClick={() => { setShowProductSearch(!showProductSearch); if (!showProductSearch) handleSearchProducts() }}>
                        <IconEdit size={16} />
                    </ActionIcon>
                </Group>
                <Divider my="xs" />
                {formData.product.id ? (
                    <Text fw={700} size="sm">{formData.product.id} - {formData.product.title}</Text>
                ) : <Text c="red" size="sm">Product not set yet</Text>}
                {showProductSearch && (
                    <Box mt="sm">
                        <TextInput placeholder="Search by title" size="sm" value={inputSearchProduct}
                            onChange={(e) => setInputSearchProduct(e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter') handleSearchProducts() }} />
                        {loadingProducts ? <Center py="sm"><Loader size="sm" /></Center> : (
                            <SimpleGrid cols={3} mt="xs" style={{ maxHeight: 250, overflowY: 'auto' }}>
                                {productList.map(p => (
                                    <UnstyledButton key={p.id} p="xs"
                                        style={{ borderRadius: 6, border: '1px solid #dee2e6', textAlign: 'center' }}
                                        onClick={() => { updateField('product', { id: Number(p.id), title: p.title }); setShowProductSearch(false) }}>
                                        {p.imageUrl && <Image src={p.imageUrl} h={100} w={100} fit="cover" radius="sm" mx="auto" />}
                                        <Text size="xs" tt="capitalize" mt={4} lineClamp={2}>{p.title}</Text>
                                    </UnstyledButton>
                                ))}
                            </SimpleGrid>
                        )}
                    </Box>
                )}
            </Card>

            <Group justify="flex-end" mt="lg">
                <Button loading={loadingSave} onClick={handleSave}>Save</Button>
            </Group>
        </Box>
    )
}

export default FormInventory
